import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FiMapPin, FiLoader } from 'react-icons/fi';
import { fullUrl, OG_IMAGE, hreflangLinks, getLocale, SITE_NAME, buildBreadcrumbSchema } from '../utils/seo';
import { locations as fallbackLocations, therapists as fallbackTherapists } from '../data/therapists';
import { useTranslation, useLocalizedData, useLanguage } from '../i18n';
import { getCurrentPosition } from '../utils/geolocation';
import TherapistCard from '../components/TherapistCard';
import api from '../utils/api';
import './TherapistListPage.css';

function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function TherapistListPage() {
  const [activeTab, setActiveTab] = useState('all');
  const { t } = useTranslation();
  const { localize } = useLocalizedData();
  const { lang } = useLanguage();

  const [locations, setLocations] = useState(fallbackLocations);
  const [therapists, setTherapists] = useState(fallbackTherapists);
  const [userCoords, setUserCoords] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsDetected, setGpsDetected] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [locRes, thRes] = await Promise.all([
          api.get('/locations'),
          api.get('/therapists'),
        ]);
        const apiLocations = locRes.data.map(l => ({
          id: l.id,
          name: { ko: l.nameKo, en: l.nameEn, zh: l.nameZh, ja: l.nameJa, th: l.nameTh, fr: l.nameFr, de: l.nameDe, es: l.nameEs },
          description: { ko: l.descKo, en: l.descEn, zh: l.descZh, ja: l.descJa, th: l.descTh, fr: l.descFr, de: l.descDe, es: l.descEs },
          image: l.image, lat: l.lat, lng: l.lng,
        }));
        const apiTherapists = thRes.data.map(t => ({
          id: t.id, name: t.name,
          nameKo: { ko: t.nameKo, en: t.nameEn, zh: t.nameZh, ja: t.nameJa, th: t.nameTh, fr: t.nameFr, de: t.nameDe, es: t.nameEs },
          bio: { ko: t.bioKo, en: t.bioEn, zh: t.bioZh, ja: t.bioJa, th: t.bioTh, fr: t.bioFr, de: t.bioDe, es: t.bioEs },
          features: t.features ? { ko: t.featuresKo, en: t.featuresEn, zh: t.featuresZh, ja: t.featuresJa, th: t.featuresTh, fr: t.featuresFr, de: t.featuresDe, es: t.featuresEs } : null,
          age: t.age, experience: t.experience, location: t.locationId,
          specialties: t.specialtyIds || [], rating: t.rating, photo: t.photo,
          height: t.height, weight: t.weight,
        }));
        setLocations(apiLocations);
        setTherapists(apiTherapists);
      } catch (err) {
        console.log('API unavailable, using fallback data');
      }
    };
    fetchData();
  }, []);

  const handleGpsDetect = async () => {
    setGpsLoading(true);
    try {
      const coords = await getCurrentPosition();
      setUserCoords(coords);
      setGpsDetected(true);

      // Find nearest location and auto-select that tab
      let minDist = Infinity;
      let nearestId = 'all';
      for (const loc of locations) {
        const dist = haversineDistance(coords.lat, coords.lng, loc.lat, loc.lng);
        if (dist < minDist) {
          minDist = dist;
          nearestId = loc.id;
        }
      }
      setActiveTab(nearestId);
    } catch (err) {
      // silently fail, user can manually select
    } finally {
      setGpsLoading(false);
    }
  };

  const tabs = [
    { id: 'all', label: t('tab_all') },
    ...locations.map((l) => ({ id: l.id, label: localize(l.name) })),
  ];

  const filtered =
    activeTab === 'all'
      ? therapists
      : therapists.filter((th) => th.location === activeTab);

  // Sort by distance if GPS is available
  const sorted = userCoords
    ? [...filtered].sort((a, b) => {
        const locA = locations.find((l) => l.id === a.location);
        const locB = locations.find((l) => l.id === b.location);
        if (!locA || !locB) return 0;
        const distA = haversineDistance(userCoords.lat, userCoords.lng, locA.lat, locA.lng);
        const distB = haversineDistance(userCoords.lat, userCoords.lng, locB.lat, locB.lng);
        return distA - distB;
      })
    : filtered;

  return (
    <div className="therapist-list-page">
      <Helmet>
        <html lang={lang} />
        <title>{t('seo_therapists_title')}</title>
        <meta name="description" content={t('seo_therapists_desc')} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={t('seo_therapists_title')} />
        <meta property="og:description" content={t('seo_therapists_desc')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={fullUrl('/therapists')} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:locale" content={getLocale(lang)} />
        <link rel="canonical" href={fullUrl('/therapists')} />
        {hreflangLinks('/therapists').map(({ rel, hreflang, href }) => (
          <link key={hreflang} rel={rel} hrefLang={hreflang} href={href} />
        ))}
        <script type="application/ld+json">{JSON.stringify(buildBreadcrumbSchema([
          { name: t('nav_home'), url: fullUrl('/') },
          { name: t('nav_therapists'), url: fullUrl('/therapists') },
        ]))}</script>
      </Helmet>
      <div className="tl-container">
        <div className="tl-header">
          <h1 className="tl-title">{t('tl_title')}</h1>
          <p className="tl-subtitle">{t('tl_subtitle')}</p>
        </div>

        <div className="tl-gps-section">
          <button
            className={`tl-gps-btn ${gpsLoading ? 'loading' : ''} ${gpsDetected ? 'detected' : ''}`}
            onClick={handleGpsDetect}
            disabled={gpsLoading}
          >
            {gpsLoading ? (
              <><FiLoader className="spin" /> {t('gps_loading')}</>
            ) : gpsDetected ? (
              <><FiMapPin /> {t('gps_nearby_active')}</>
            ) : (
              <><FiMapPin /> {t('gps_find_nearby')}</>
            )}
          </button>
        </div>

        <div className="tl-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tl-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              <span className="tab-count">
                {tab.id === 'all'
                  ? therapists.length
                  : therapists.filter((th) => th.location === tab.id).length}
              </span>
            </button>
          ))}
        </div>

        {/* Notice section */}
        <div className="tl-notice">
          <div className="tl-notice-item">
            <span className="notice-icon">⏰</span>
            <span>{t('notice_punctual')}</span>
          </div>
          <div className="tl-notice-item warning">
            <span className="notice-icon">🚫</span>
            <span>{t('notice_no_sexual')}</span>
          </div>
        </div>

        <div className="tl-grid">
          {sorted.map((th) => {
            const loc = locations.find((l) => l.id === th.location);
            return (
              <div key={th.id} className="tl-card-wrapper">
                <TherapistCard therapist={th} showDetails onClick={() => {}} />
                <div className="tl-card-location">
                  {loc?.image} {localize(loc?.name)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default TherapistListPage;
